import { useMemo } from "react";
import { MetricCardsContainer } from "@/components/metrics/metric-cards-container";
import type { MetricCardConfig } from "@/components/metrics/types";
import type { CustomChannelMetrics } from "@/hooks/api/use-custom-channels";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import { useCustomChannelSelection } from "@/hooks/use-custom-channel-selection";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { formatCurrency } from "@/lib/metrics/format-currency";
import { formatNumber } from "@/lib/metrics/format-number";
import {
  DEFAULT_METRIC_ORDER,
  HIDDEN_METRICS,
  METRIC_CONFIG,
} from "@/pages/custom-channels/custom-channels-list/custom-channels-metrics-config";

interface CustomChannelMetricCardsProps {
  metrics: CustomChannelMetrics | undefined;
  onMetricSelect: (metric: string) => void;
  loadingStates: Record<string, { isLoading: boolean; isError: boolean }>;
  leftControls?: React.ReactNode;
  showBody?: boolean;
  emptyBody?: React.ReactNode;
}

export function CustomChannelMetricCards({
  metrics,
  onMetricSelect,
  loadingStates,
  leftControls,
  showBody,
  emptyBody,
}: CustomChannelMetricCardsProps) {
  const { selectedSubchannel } = useCustomChannelSelection();
  const filterState = useChannelFilterState("custom-channels::explorer");
  const {
    displayFilters,
    filterColumns,
    getTotalFilterCount,
    handleClearFilter,
    clearAllFilters,
  } = useMetricCardFilters(filterState);

  // Dynamic title based on whether a subchannel is selected
  const title = selectedSubchannel
    ? "Top-level sub-channel metrics"
    : "Top-level channel metrics";

  // Convert METRIC_CONFIG to the format expected by MetricCardsContainer
  const convertedMetricConfig = useMemo(() => {
    const config: Record<string, MetricCardConfig> = {};

    Object.entries(METRIC_CONFIG).forEach(([key, value]) => {
      if (value) {
        config[key] = {
          title: value.title,
          tooltip: value.tooltip,
          category: value.category,
          // Pass through the render function from METRIC_CONFIG if it exists
          render: value.render,
        };
      }
    });

    return config;
  }, []);

  // Filter out hidden metrics from display
  const visibleMetrics = DEFAULT_METRIC_ORDER.filter(
    (metric) => !HIDDEN_METRICS.includes(metric),
  );

  // loadingStates is already in the correct format
  const convertedLoadingStates = loadingStates;

  // Default/fallback renderMetric function for metrics without custom render functions
  const defaultRenderMetric = (
    value: number | null,
    options: Record<string, unknown>,
  ) => {
    const metricKey = options.metricKey as keyof CustomChannelMetrics;

    // Determine if metric should be formatted as currency
    const isCurrency = metricKey?.toString().includes("amount");

    const safeValue = value ?? 0;

    if (isCurrency) {
      return formatCurrency(safeValue);
    }
    return formatNumber(safeValue);
  };

  return (
    <MetricCardsContainer
      metrics={metrics}
      metricConfig={convertedMetricConfig}
      defaultOrder={visibleMetrics}
      hiddenMetrics={HIDDEN_METRICS as string[]}
      wipMetrics={[]} // No WIP metrics for custom channels
      storageKey="custom-channels::explorer::metric-order"
      title={title}
      entityScope="custom-channels::explorer"
      entity="channel"
      renderMetric={defaultRenderMetric}
      onMetricSelect={onMetricSelect}
      loadingStates={convertedLoadingStates}
      leftControls={leftControls}
      showBody={showBody}
      emptyBody={emptyBody}
      displayFilters={displayFilters}
      filterColumns={filterColumns}
      getTotalFilterCount={getTotalFilterCount}
      clearFilter={handleClearFilter}
      onResetMetricFilters={clearAllFilters}
    />
  );
}
